import type { BookableServiceRecord, OpenOrderRecord } from '@navio/nsdk';
import { invalidateQuery, noddiAsync, URLKeys } from 'noddi-async';
import { resolveStationaryServicePolicy } from 'noddi-util';
import { useAuthContext } from 'noddi-web';
import { useEffect, useRef } from 'react';
import { useServiceCenter, useStationaryBookingCar } from '../stores/StationaryBookingStore';
import type { StationaryService } from '../types/stationaryBooking';

const toService = (s: BookableServiceRecord): StationaryService => ({
  salesItemId: s.salesItemId,
  name: s.name,
  grossPrice: s.grossPrice,
  originalGrossPrice: s.originalGrossPrice ?? null,
  priceListName: s.priceListName ?? null,
  discountPercent: s.discountPercent ?? null,
  // Locked open-order primary / catalog flag; default editable when API omits the field.
  isCustomerEditable: s.isCustomerEditable ?? true,
  bookingCategoryType: s.bookingCategoryType,
  isWheelPickupRequired: s.isWheelPickupRequired,
  isIncomingTireSeasonRequired: s.isIncomingTireSeasonRequired
});

/** Adapter-agnostic bookable services + open-order locks for stationary booking. */
export const useStationaryBookableServices = () => {
  const { isLoggedIn } = useAuthContext();
  const serviceCenter = useServiceCenter();
  const selectedCar = useStationaryBookingCar();
  const licensePlateNumber = selectedCar?.licensePlate.number;
  const serviceDepartmentId = serviceCenter?.serviceDepartmentId;

  // Owner-only fields (tireHotelAtOtherDepartment) are stripped from the
  // anonymous cache — refetch this endpoint when login flips, not the world.
  const wasLoggedIn = useRef(isLoggedIn);
  useEffect(() => {
    if (isLoggedIn && !wasLoggedIn.current) {
      void invalidateQuery({ urlKey: URLKeys.getStationaryBookableServices });
    }
    wasLoggedIn.current = isLoggedIn;
  }, [isLoggedIn]);

  // Require plate: adapter-backed departments reject missing plate with 400,
  // and prices/hotel filtering are always plate-specific.
  const canFetch = serviceDepartmentId != null && Boolean(licensePlateNumber);

  // isLoading (not isPending): query is gated by `enabled`
  const { data, error, isLoading } = noddiAsync.useGet({
    type: URLKeys.getStationaryBookableServices,
    input: {
      serviceDepartmentId: serviceDepartmentId!,
      licensePlateNumber: licensePlateNumber!
    },
    queryConfig: { enabled: canFetch }
  });

  const services = data?.services.map(toService);

  const policy = resolveStationaryServicePolicy(services ?? [], data?.lockedSalesItemId ?? null);

  const addonServicesBySalesItemId = (data?.addonServices ?? []).reduce<Record<string, StationaryService[]>>(
    (acc, addon) => {
      if (addon.addonOfSalesItemId == null) {
        return acc;
      }
      const key = String(addon.addonOfSalesItemId);
      (acc[key] ??= []).push(toService(addon));
      return acc;
    },
    {}
  );

  const openOrder: OpenOrderRecord | null = data?.openOrder ?? null;

  return {
    services,
    nonEditableServices: policy.nonEditable,
    selectableServices: policy.selectable,
    enforcedPrimary: policy.enforcedPrimary,
    addonServicesBySalesItemId,
    openOrder,
    lockedSalesItemId: data?.lockedSalesItemId ?? null,
    priceListName: data?.priceListName ?? null,
    discountPercent: data?.discountPercent ?? null,
    tireHotelAtOtherDepartment: data?.tireHotelAtOtherDepartment ?? null,
    isLoading,
    error
  };
};
